import { GanttZoomScale, GanttZoomScaleMax } from "@ms/uno-constants/lib/local/GanttZoomConstants";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
/**
 * Constants for Gantt controls
 * Extracted from pexui to make components standalone
 */ /**
 * Layer z-index values for SVG element stacking order
 * Higher values appear above lower values
 */ export const GanttLayerZIndex = {
    /** Non-working time segments (weekends, holidays) */ nonWorkingSegment: 0,
    /** Due date indicator horizontal leader line (due date indicator horizontal line is below link lines by design) */ dueDateIndicatorHorizontalLine: 1,
    /** Dependency link lines between tasks (kept below task bars) */ linkLine: 2,
    /** Regular task bars */ taskBar: 3,
    /** Due date indicator diamond + vertical tick rendered above task bars */ dueDateIndicatorDiamondAndVerticalLine: 4,
    /** Task bar being added/created */ addTaskBar: 5,
    /** Side grid lines */ sideGrid: 6,
    /** Today indicator line */ todayLine: 7
};
/**
 * Width/height in pixels of the due date diamond marker (a square rotated 45°).
 * Matches the Figma spec (8px square, ~11.31px diagonal bounding box).
 */ export const DUE_DATE_DIAMOND_SIZE = 8;
/** Border thickness in pixels of the due date diamond (solid, drawn in the surface color). */ export const DUE_DATE_DIAMOND_BORDER = 1;
/** Thickness in pixels of the leader line connecting a task bar edge to the due date diamond. */ export const DUE_DATE_LEADER_LINE_THICKNESS = 1;
/**
 * Height in pixels of the vertical tick line drawn through the due date diamond. Slightly taller
 * than the 20px task bar so it reads as a date marker poking above and below the bar.
 */ export const DUE_DATE_VERTICAL_LINE_HEIGHT = 24;
/** Brand-colored core thickness in pixels of the due date vertical tick line. */ export const DUE_DATE_VERTICAL_LINE_THICKNESS = 1;
/**
 * Standard row height in pixels for gantt chart rows
 */ export const GanttRowHeight = 40;
/**
 * Height in pixels of the AddTaskRow footer rendered below the left-panel grid.
 * Used as GanttChart's bottomPadding so the chart's scroll range matches the panel's.
 */ export const ADD_TASK_ROW_HEIGHT = 44;
/** Minimum cap width for the timescale hover overlay end caps */ export const MIN_OVERLAY_CAP_WIDTH = 20;
/** Minimum task bar width in pixels */ export const MIN_TASK_BAR_WIDTH = 2;
/** Minimum width for single-date task bars */ export const MIN_SINGLE_DATE_TASK_BAR_WIDTH = 3;
/** Minimum rendered bar width (px) at which left/right resize expanders are shown on hover */ export const MIN_BAR_WIDTH_FOR_EXPANDERS = 24;
/**
 * Maps each zoom scale to a day width in pixels.
 *
 * | Scale        | px/day | Label type   |
 * |--------------|--------|--------------|
 * | SMALL (week) | 30     | weekly       |
 * | MEDIUM (2wk) | 24     | biweekly     |
 * | LARGE (mo)   | 18     | monthly      |
 * | SIXQUARTERS  | 10     | quarterly    |
 * | TWOYEARS     | 6      | twoyears     |
 * | THREEYEARS   | 3      | threeyears   |
 * | SIXYEARS     | 1      | sixyears     |
 */ export const DAY_WIDTH_FOR_SCALE = {
    [GanttZoomScale.SMALL]: 30,
    [GanttZoomScale.MEDIUM]: 24,
    [GanttZoomScale.LARGE]: 18,
    [GanttZoomScale.SIXQUARTERS]: 10,
    [GanttZoomScale.TWOYEARS]: 6,
    [GanttZoomScale.THREEYEARS]: 3,
    [GanttZoomScale.SIXYEARS]: 1
};
/**
 * Minimum rendered bar width (px) at which the task name is painted inside the bar: one day at the
 * most zoomed-in scale. So a single-day task carries its name only when the user is zoomed all the
 * way in, and reads as a bare bar at every wider scale.
 *
 * Below this the label has too little room to say anything: it reserves 4px of horizontal padding on
 * each side, leaving space for roughly three caption2Strong characters before it degrades to a bare
 * ellipsis or a sliver of one letter, which reads as noise rather than information. Zoomed-out
 * scales make that the common case (at SIXYEARS a day is 1px wide). The full name stays available
 * from the bar's tooltip and its grid row.
 */ export const MIN_BAR_WIDTH_FOR_LABEL = DAY_WIDTH_FOR_SCALE[GanttZoomScaleMax];
/**
 * Maps each zoom scale to a single-date task (with only start or finish, not both) bar width in pixels.
 */ export const SINGLE_DATE_BAR_WIDTH_FOR_SCALE = {
    [GanttZoomScale.SMALL]: 24,
    [GanttZoomScale.MEDIUM]: 20,
    [GanttZoomScale.LARGE]: 16,
    [GanttZoomScale.SIXQUARTERS]: 10,
    [GanttZoomScale.TWOYEARS]: 6,
    [GanttZoomScale.THREEYEARS]: MIN_SINGLE_DATE_TASK_BAR_WIDTH,
    [GanttZoomScale.SIXYEARS]: MIN_SINGLE_DATE_TASK_BAR_WIDTH
};
/** Cap width for multi-day tasks (two caps with connecting line) */ export const CAP_WIDTH_FOR_SCALE = {
    [GanttZoomScale.SMALL]: 30,
    [GanttZoomScale.MEDIUM]: 24,
    [GanttZoomScale.LARGE]: MIN_OVERLAY_CAP_WIDTH,
    [GanttZoomScale.SIXQUARTERS]: MIN_OVERLAY_CAP_WIDTH,
    [GanttZoomScale.TWOYEARS]: MIN_OVERLAY_CAP_WIDTH,
    [GanttZoomScale.THREEYEARS]: MIN_OVERLAY_CAP_WIDTH,
    [GanttZoomScale.SIXYEARS]: MIN_OVERLAY_CAP_WIDTH
};
/** Cap width for start-only or end-only tasks (half-pill, narrower at SMALL) */ export const SINGLE_DATE_CAP_WIDTH_FOR_SCALE = {
    [GanttZoomScale.SMALL]: 24,
    [GanttZoomScale.MEDIUM]: MIN_OVERLAY_CAP_WIDTH,
    [GanttZoomScale.LARGE]: MIN_OVERLAY_CAP_WIDTH,
    [GanttZoomScale.SIXQUARTERS]: MIN_OVERLAY_CAP_WIDTH,
    [GanttZoomScale.TWOYEARS]: MIN_OVERLAY_CAP_WIDTH,
    [GanttZoomScale.THREEYEARS]: MIN_OVERLAY_CAP_WIDTH,
    [GanttZoomScale.SIXYEARS]: MIN_OVERLAY_CAP_WIDTH
};
/** Minimum visible overlay width at week zoom (caps are wider so minimum is larger) */ export const MIN_OVERLAY_WIDTH_WEEK = 72;
/** Minimum visible overlay width at all other zoom levels */ export const MIN_OVERLAY_WIDTH_DEFAULT = 40;
/**
 * Height in pixels for regular task bars
 */ export const GanttTaskBarHeight = 20;
/**
 * Maximum height in pixels for summary task bars (when hovered)
 */ export const GanttTaskSummaryMaxHeight = 8;
/**
 * Minimum height in pixels for summary task bars (default state)
 */ export const GanttTaskSummaryMinHeight = 2;
/**
 * Width in pixels for milestone diamond shape
 */ export const TaskMilestoneWidth = 15;
/**
 * Horizontal padding in pixels added to both sides of task bars for hover areas and link anchors
 * Used to position elements relative to task bar edges
 */ export const LinkOffset = 8;
/**
 * Horizontal offset in pixels for positioning the left/right expander handles
 * Used for drag handles that resize task bars
 */ export const ExpanderHorizontalOffset = 5;
/**
 * Horizontal offset in pixels for positioning milestone icon from the center
 */ export const MilestoneIconOffset = 10;
/**
 * Length in pixels of the ghost-dependency stub link line, and the matching gap between
 * the visible task bar and the ghost pill in GanttTagItemsView. Matches UNO's
 * `UNFILTERED_TASKS_LINK_LINE`.
 */ export const UnfilteredTasksLinkLine = 33.5;
/**
 * Fixed render width in pixels for each ghost-dependency pill in GanttTagItemsView.
 * Kept constant (not zoom-dependent) so the pill stays legible at every zoom level.
 */ export const GhostTaskWidth = 60;
/**
 * Horizontal padding (px) on each side of a ghost pill inside its fixed-width slot.
 * Produces visual spacing between adjacent ghost pills (2 * this value between any two).
 * The slot stride stays at GhostTaskWidth so link-line stub positioning is unaffected.
 */ export const GhostTaskSlotPadding = 4;
/**
 * Minimum width in pixels for task bars when hovered
 * Ensures sufficient clickable area for narrow tasks
 */ export const GanttMinHoveredTaskBarWidth = 50;
/**
 * Source of a task drag operation
 * Used to determine which part of the task is being dragged
 */ export var DragTaskSource = /*#__PURE__*/ function(DragTaskSource) {
    /** No drag operation in progress */ DragTaskSource["None"] = "none";
    /** Dragging the task bar body (moves entire task) */ DragTaskSource["TaskBar"] = "taskBar";
    /** Dragging the left edge handle (changes start date) */ DragTaskSource["LeftExpander"] = "leftExpander";
    /** Dragging the right edge handle (changes end date) */ DragTaskSource["RightExpander"] = "rightExpander";
    return DragTaskSource;
}({});
/**
 * Position of link line anchor points on task bars
 * Used for creating dependency relationships between tasks
 */ export var LinkLineAnchor = /*#__PURE__*/ function(LinkLineAnchor) {
    /** Anchor point on the left side of the task bar */ LinkLineAnchor["LeftAnchor"] = "leftAnchor";
    /** Anchor point on the right side of the task bar */ LinkLineAnchor["RightAnchor"] = "rightAnchor";
    return LinkLineAnchor;
}({});
/**
 * Marker dimension constants for SVG arrow markers
 */ export const LINK_LINE_MARKER_SIZE = 10;
export const LINK_LINE_MARKER_REF_X = 2;
export const LINK_LINE_MARKER_REF_Y = 5;
/**
 * Maps each zoom scale to a human-readable label.
 * Used for aria-valuetext and tooltip content on the ZoomSlider.
 * TypeScript enforces exhaustiveness — adding a new GanttZoomScale value without
 * updating this map will produce a compile error.
 */ const GANTT_ZOOM_LEVEL_LABELS = {
    [GanttZoomScale.SMALL]: Strings.ZoomSlider_WeekLabel,
    [GanttZoomScale.MEDIUM]: Strings.ZoomSlider_TwoWeekLabel,
    [GanttZoomScale.LARGE]: Strings.ZoomSlider_MonthLabel,
    [GanttZoomScale.SIXQUARTERS]: Strings.ZoomSlider_SixQuartersLabel,
    [GanttZoomScale.TWOYEARS]: Strings.ZoomSlider_TwoYearsLabel,
    [GanttZoomScale.THREEYEARS]: Strings.ZoomSlider_ThreeYearsLabel,
    [GanttZoomScale.SIXYEARS]: Strings.ZoomSlider_SixYearsLabel
};
/**
 * Returns a human-readable label for a given zoom scale.
 * Used for aria-valuetext and tooltip content on the ZoomSlider.
 */ export function getGanttZoomLevelLabel(scale) {
    return GANTT_ZOOM_LEVEL_LABELS[scale];
}
// ============================================================================
// Link line path calculation constants (ported from pexui GanttSvgUtilities)
// ============================================================================
/** Arc radius for rounded corners on link line paths */ export const LINK_ARC_RADIUS = 7;
/** Diameter of arc, computed from LINK_ARC_RADIUS */ export const LINK_ARC_DIAMETER = 2 * LINK_ARC_RADIUS;
/** Y-offset from top of row to the source anchor point on task bars */ export const SOURCE_Y_OFFSET = 20;
/** Y-offset from top of row to the target anchor point (arrow tip) */ export const TARGET_Y_OFFSET = 20;
/** Horizontal offset for the end arrow to prevent overlap */ export const END_ARROW_OFFSET = 3;
/** Horizontal space reserved for the end arrow marker */ export const END_ARROW_SPACE = LINK_LINE_MARKER_SIZE / 2 - LINK_LINE_MARKER_REF_X;
/** Horizontal offset from anchor to first vertical segment */ export const LINK_LINE_START_POINT_OFFSET = 11;
/** Vertical offset to avoid link lines overlapping task bars */ export const LINK_LINE_Y_OFFSET_FOR_NON_COLLISION = 11;
/** Minimum horizontal distance before a FinishToStart link wraps around */ export const MIN_DISTANCE_OFFSET = 36;
/** Default stroke width for dependency link lines */ export const LINK_LINE_STROKE_WIDTH = 1;
// ─── Link anchor hitbox & visual positioning ───────────────────────────
/** Width of the invisible anchor hitbox in pixels */ export const ANCHOR_HITBOX_WIDTH = 24;
/** Height of the invisible anchor hitbox in pixels */ export const ANCHOR_HITBOX_HEIGHT = 21;
/** Right offset for anchor visuals on regular task bars */ export const ANCHOR_VISUAL_OFFSET = 25;
/** Right offset for anchor visuals on milestones in RTL mode */ export const ANCHOR_VISUAL_OFFSET_MILESTONE_RTL = 33;
/** Right offset for the anchor dot on regular task bars */ export const ANCHOR_DOT_OFFSET = 19;
/** Right offset for the anchor dot on milestones in RTL mode */ export const ANCHOR_DOT_OFFSET_MILESTONE_RTL = 27;
/** Top offset for anchors on summary rows */ export const ANCHOR_TOP_SUMMARY = -8;
/** Top offset for anchors on regular/milestone rows */ export const ANCHOR_TOP_DEFAULT = -2;
/** Vertical margin adjustment for the anchor dot visual */ export const ANCHOR_DOT_MARGIN_TOP = -1;
/** Horizontal buffer (px) on each side of the viewport for canvas clipping */ export const CanvasHorizontalBuffer = 100;
/**
 * Pixel offset applied when scrolling to a task bar so it lands with visual breathing room
 * rather than flush at the left edge of the viewport. Matches pexui's DEFAULT_GAP_BETWEEN_GRIDGANTT.
 * Dividing by dayWidth and rounding gives ~90px of visual offset at any zoom level.
 */ export const SCROLL_TO_TASK_OFFSET_PX = 90;
/** Number of extra rows above/below the visible area for canvas vertical clipping */ export const CanvasVerticalBufferRows = 75;
